import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import ListItemText from '@material-ui/core/ListItemText';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { BackstageTheme } from '@backstage/theme';
import { Link as RouterLink, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  listItem: {
    paddingLeft: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(1.5),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    transition: theme.transitions.create(['background-color', 'padding', 'width'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  listItemIcon: {
    minWidth: 36,
    marginRight: theme.spacing(1),
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  nestedItem: {
    paddingLeft: theme.spacing(4),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(3.5),
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #FFDD00',
      paddingLeft: theme.spacing(3.5),
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  nestedItemIcon: {
    minWidth: 28,
    marginRight: theme.spacing(1),
    '& svg': {
      fontSize: '18px', // Iconos más pequeños para subitems cuando expandido
    },
  },
  expandIcon: {
    marginLeft: 'auto',
  },
  subheader: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    fontWeight: 500,
  },
}));

export interface SubMenuItem {
  text: string;
  path: string;
  icon?: React.ReactNode;
}

export interface PichinchaSubmenuItemProps {
  text: string;
  icon: React.ReactNode;
  subItems: SubMenuItem[];
  isActive?: boolean;
}

export const PichinchaSubmenuItem: React.FC<PichinchaSubmenuItemProps> = ({
  text,
  icon,
  subItems,
  isActive = false,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const [open, setOpen] = useState(isActive);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem 
        button 
        onClick={handleClick}
        className={classes.listItem}
        selected={isActive}
        style={{
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: 'auto'
        }}
      >
        <div className={classes.listItemIcon}>
          {icon}
        </div>
        <ListItemText primary={text} />
        {open ? <ExpandLessIcon className={classes.expandIcon} /> : <ExpandMoreIcon className={classes.expandIcon} />}
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {subItems.map((item, index) => {
            const isSubItemSelected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <ListItem
                key={index}
                button
                component={RouterLink}
                to={item.path}
                className={classes.nestedItem}
                selected={isSubItemSelected}
              >
                {item.icon && (
                  <div className={classes.nestedItemIcon}>
                    {item.icon}
                  </div>
                )}
                <ListItemText primary={item.text} />
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};
